import { QueryClient } from "@tanstack/react-query";

/**
 * The query defaults the app ships with.
 *
 * Exported rather than written inline in `main.tsx` so the test harness
 * can mirror them by importing. Copying them is how a suite drifts back
 * into passing while the shipped page behaves differently: the billing
 * reads served a five-minute-old view of the payment provider through a
 * green suite, because the harness left `staleTime` at React Query's
 * default of `0` and every query in every test was therefore permanently
 * stale.
 *
 * `main.tsx` cannot be that source — importing it runs `initOtel()` and
 * mounts the app.
 *
 * A hook that needs different freshness overrides it and says why; see
 * `hooks/useBilling.ts`.
 */

/**
 * A 4xx is a verdict about the request, not a blip — 403
 * `AUTH-MISSING-TENANT` for a JWT with no tenant row, 404 for a row that
 * is gone — so re-issuing it can only fail the same way while doubling
 * the traffic on every authenticated page load, and it lands the reader
 * behind a "Try again" that can never succeed. 5xx and transport
 * failures keep the single retry.
 *
 * Structural rather than `instanceof`: every API client throws an error
 * carrying `status` (`BillingApiError`, `TenantApiError`), and the
 * defaults must not depend on any one of them.
 *
 * A default rather than a per-hook option for the same reason the rest
 * of this object is: a hook-level `retry` outranks the client's, so
 * setting it on the hook would also outrank the harness's `retry:
 * false` and quietly disable that hook's failure-path tests.
 */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
  const { status } = error as { status?: unknown };
  if (typeof status === "number" && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

export const QUERY_DEFAULTS = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
  retry: retryUnlessClientError,
  refetchOnWindowFocus: true,
} as const;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: { queries: { ...QUERY_DEFAULTS } },
  });
}
